import React, { useEffect, useMemo, useState } from 'react';
import PayoutService from '../../services/PayoutService';
import BranchService from '../../services/BranchService';
import StaffService from '../../services/StaffService';
import MemberService from '../../services/MemberService';
import VendorService from '../../services/VendorService';
import ProjectService from '../../services/ProjectService';
import ExpenditureTypeService from '../../services/ExpenditureTypeService';
import utility from '../../utils/utility';
import ReactPaginate from 'react-paginate';
import FilterBar from '../../components/FilterBar';
import { filterList } from '../../utils/filterUtils';

const Payout = () => {
    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [searchText, setSearchText] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [filterBranch, setFilterBranch] = useState('');
    const [filterProject, setFilterProject] = useState('');
    const [filterVendor, setFilterVendor] = useState('');
    const [filterPaymentMode, setFilterPaymentMode] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Dropdown data
    const [branches, setBranches] = useState([]);
    const [staffs, setStaffs] = useState([]);
    const [members, setMembers] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [projects, setProjects] = useState([]);
    const [expenditureTypes, setExpenditureTypes] = useState([]);

    const handleFilterChange = (setter) => (event) => {
        setter(event.target.value);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setSearchText('');
        setFilterDateFrom('');
        setFilterDateTo('');
        setFilterBranch('');
        setFilterProject('');
        setFilterVendor('');
        setFilterPaymentMode('');
        setFilterStatus('');
        setCurrentPage(1);
    };

    useEffect(() => {
        fetchDropdownData();
    }, []);

    useEffect(() => {
        fetchPayouts();
    }, [currentPage, filterDateFrom, filterDateTo, filterBranch, filterProject, filterVendor, filterPaymentMode, filterStatus]);

    const [formData, setFormData] = useState({
        id: '',
        branchId: '',
        entityType: 'staff', // staff, member, vendor
        staffId: '',
        memberId: '',
        vendorId: '',
        projectId: '',
        expenditureTypeId: '',
        paymentDate: '',
        amount: '',
        paymentMode: 'Cash',
        transactionNo: '',
        remarks: '',
        transactionImage: '',
        status: 'completed',
        isActive: 1
    });
    const filteredPayouts = useMemo(() => payouts.filter((payout) => {
        if (!searchText) return true;
        return filterList([payout], searchText, ['paymentMode', 'transactionNo', 'projectName', 'branchName', 'status', 'memberName', 'staffName', 'vendorName']).length > 0;
    }), [payouts, searchText]);

    const fetchPayouts = async () => {
        try {
            setLoading(true);
            const data = await PayoutService.getAllPayouts(currentPage, itemsPerPage, {
                branchId: filterBranch,
                projectId: filterProject,
                vendorId: filterVendor,
                paymentMode: filterPaymentMode,
                status: filterStatus,
                startDate: filterDateFrom,
                endDate: filterDateTo
            });
            setPayouts(data.data || []);
            setPageCount(data.pagination?.totalPages || 0);
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDropdownData = async () => {
        try {
            const [branchRes, staffRes, memberRes, vendorRes, projectRes, expRes] = await Promise.all([
                BranchService.getAllBranches(1, 100),
                StaffService.getAll(1, 100),
                MemberService.getAll(1, 100),
                VendorService.getAll(1, 100),
                ProjectService.getAll(1, 100),
                ExpenditureTypeService.getAll(1, 100)
            ]);
            setBranches(branchRes.data || []);
            setStaffs(staffRes.data || []);
            setMembers(memberRes.data || []);
            setVendors(vendorRes.data || []);
            setProjects(projectRes.data || []);
            setExpenditureTypes(expRes.data || []);
        } catch (err) {
            console.error('Failed to fetch dropdown data', err);
        }
    };

    const handleOpenModal = (payout = null) => {
        if (payout) {
            setEditMode(true);
            let entityType = 'staff';
            if (payout.memberId) entityType = 'member';
            else if (payout.vendorId) entityType = 'vendor';

            setFormData({
                id: payout.id,
                branchId: payout.branchId || '',
                entityType: entityType,
                staffId: payout.staffId || '',
                memberId: payout.memberId || '',
                vendorId: payout.vendorId || '',
                projectId: payout.projectId || '',
                expenditureTypeId: payout.expenditureTypeId || '',
                paymentDate: payout.paymentDate ? payout.paymentDate.split('T')[0] : '',
                amount: payout.amount || '',
                paymentMode: payout.paymentMode || 'Cash',
                transactionNo: payout.transactionNo || '',
                remarks: payout.remarks || '',
                transactionImage: payout.transactionImage || '',
                status: payout.status || 'completed',
                isActive: payout.isActive
            });
        } else {
            setEditMode(false);
            setFormData({
                id: '',
                branchId: '',
                entityType: 'staff',
                staffId: '',
                memberId: '',
                vendorId: '',
                projectId: '',
                expenditureTypeId: '',
                paymentDate: new Date().toISOString().split('T')[0],
                amount: '',
                paymentMode: 'Cash',
                transactionNo: '',
                remarks: '',
                transactionImage: '',
                status: 'completed',
                isActive: 1
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'isActive' ? parseInt(value) : value,
            ...(name === 'entityType' ? { staffId: '', memberId: '', vendorId: '' } : {})
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const payload = { ...formData };
            if (formData.entityType === 'staff') { payload.memberId = null; payload.vendorId = null; }
            else if (formData.entityType === 'member') { payload.staffId = null; payload.vendorId = null; }
            else if (formData.entityType === 'vendor') { payload.staffId = null; payload.memberId = null; }

            if (editMode) {
                delete payload.entityType; // Remove entityType before sending to API
                await PayoutService.updatePayout(formData.id, payload);
                utility.showSuccess('Payout transaction updated successfully');
            } else {
                await PayoutService.createPayout(payload);
                utility.showSuccess('Payout transaction created successfully');
            }
            handleCloseModal();
            fetchPayouts();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (payout) => {
        try {
            const newStatus = payout.isActive ? 0 : 1;
            await PayoutService.updateStatus(payout.id, newStatus);
            utility.showSuccess(`Payout status ${newStatus ? 'activated' : 'deactivated'} successfully`);
            fetchPayouts();
        } catch (err) {
            utility.showError(err.message);
        }
    };

    const handlePageClick = (event) => {
        setCurrentPage(event.selected + 1);
    };

    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12 text-end mb-3">
                    <button className="btn bg-gradient-dark mb-0" onClick={() => handleOpenModal()}>
                        <i className="material-symbols-rounded text-sm">add</i>&nbsp;&nbsp;Add New Payout
                    </button>
                </div>
                <div className="col-12">
                    <div className="card my-4">
                        <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                            <div className="bg-gradient-dark shadow-dark border-radius-lg pt-4 pb-3">
                                <h6 className="text-white text-capitalize ps-3">Payout Transactions</h6>
                            </div>
                        </div>
                        <div className="card-body px-0 pb-2">
                            <div className="px-3 mb-3">
                                <div className="bg-white rounded-3 shadow-sm p-3">
                                    <div className="row gy-2 gx-2 align-items-end">
                                        <div className="col-lg-3 col-md-6">
                                            <FilterBar
                                                value={searchText}
                                                onChange={setSearchText}
                                                onClear={() => setSearchText('')}
                                                placeholder="Search payouts..."
                                            />
                                        </div>

                                        <div className="col-lg-2 col-md-6">
                                            <label className="form-label text-muted small">Date From</label>
                                            <input type="date" className="form-control" value={filterDateFrom} onChange={handleFilterChange(setFilterDateFrom)} />
                                        </div>
                                        <div className="col-lg-2 col-md-6">
                                            <label className="form-label text-muted small">Date To</label>
                                            <input type="date" className="form-control" value={filterDateTo} onChange={handleFilterChange(setFilterDateTo)} />
                                        </div>
                                        <div className="col-lg-2 col-md-6">
                                            <label className="form-label text-muted small">Branch</label>
                                            <select className="form-select" value={filterBranch} onChange={handleFilterChange(setFilterBranch)}>
                                                <option value="">All Branches</option>
                                                {branches.map((branch) => (
                                                    <option key={branch.id} value={branch.id}>{branch.branchName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Project</label>
                                            <select className="form-select" value={filterProject} onChange={handleFilterChange(setFilterProject)}>
                                                <option value="">All Projects</option>
                                                {projects.map((project) => (
                                                    <option key={project.id} value={project.id}>{project.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Vendor</label>
                                            <select className="form-select" value={filterVendor} onChange={handleFilterChange(setFilterVendor)}>
                                                <option value="">All Vendors</option>
                                                {vendors.map((vendor) => (
                                                    <option key={vendor.id} value={vendor.id}>{vendor.vendorName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-1">
                                            <label className="form-label text-muted small">Mode</label>
                                            <select className="form-select" value={filterPaymentMode} onChange={handleFilterChange(setFilterPaymentMode)}>
                                                <option value="">Any</option>
                                                <option value="Cash">Cash</option>
                                                <option value="UPI">UPI</option>
                                                <option value="NEFT">NEFT</option>
                                                <option value="RTGS">RTGS</option>
                                            </select>
                                        </div>
                                        <div className="col-md-1">
                                            <label className="form-label text-muted small">Status</label>
                                            <select className="form-select" value={filterStatus} onChange={handleFilterChange(setFilterStatus)}>
                                                <option value="">Any</option>
                                                <option value="completed">Completed</option>
                                                <option value="pending">Pending</option>
                                                <option value="cancelled">Cancelled</option>
                                            </select>
                                        </div>
                                        <div className="col-lg-2 col-md-6 d-flex align-items-end">
                                            <button type="button" className="btn btn-outline-secondary w-100" onClick={resetFilters}>
                                                Clear Filters
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive p-1">
                                {loading ? (
                                    <div className="p-4 text-center">Loading...</div>
                                ) : (
                                    <table className="table table-bordered align-items-center mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Date</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Paied By</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Paid to Entity / Project</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Exp. Type</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Mode</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-end">Amount (₹)</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Status</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredPayouts.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4">No payout transactions found.</td>
                                                </tr>
                                            ) : (
                                                filteredPayouts.map((payout) => (
                                                    <tr key={payout.id}>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0 px-3">{payout.paymentDate ? payout.paymentDate.split('T')[0] : 'N/A'}</p>
                                                        </td>


                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">
                                                                {payout.createdByName || 'N/A'}
                                                            </p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">
                                                                {payout.staffName || payout.memberName || payout.vendorName || 'N/A'}
                                                            </p>
                                                            <p className="text-xs text-secondary mb-0">{payout.projectName || 'No Project'}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{payout.expenditureTypeName || 'N/A'}</p>
                                                            <p className="text-xs text-secondary mb-0">{payout.remarks || ''}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{payout.paymentMode || 'N/A'}</p>
                                                            <p className="text-xs text-secondary mb-0">{payout.transactionNo || ''}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0 text-end">{payout.amount || '0.00'}</p>
                                                        </td>
                                                        <td>
                                                            <span className={`badge badge-sm bg-gradient-${payout.isActive ? 'success' : 'secondary'} cursor-pointer`} onClick={() => handleToggleStatus(payout)}>
                                                                {payout.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td className="align-middle text-center">
                                                            <button className="btn btn-link text-info mb-0" onClick={() => handleOpenModal(payout)}>
                                                                Edit
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="7">
                                                    <ReactPaginate
                                                        forcePage={currentPage - 1}
                                                        breakLabel="..."
                                                        nextLabel=">"
                                                        onPageChange={handlePageClick}
                                                        pageRangeDisplayed={3}
                                                        marginPagesDisplayed={2}
                                                        pageCount={pageCount}
                                                        previousLabel="<"
                                                        containerClassName="pagination px-3"
                                                        pageClassName="page-item"
                                                        pageLinkClassName="page-link"
                                                        previousClassName="page-item"
                                                        previousLinkClassName="page-link"
                                                        nextClassName="page-item"
                                                        nextLinkClassName="page-link"
                                                        activeClassName="active"
                                                        disabledClassName="disabled"
                                                        breakClassName="page-item"
                                                        breakLinkClassName="page-link"
                                                    />
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title font-weight-normal">{editMode ? 'Edit Payout' : 'Add New Payout'}</h5>
                                <button type="button" className="btn-close text-dark" onClick={handleCloseModal}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body row">
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Branch</label>
                                            <select name="branchId" className="form-control" value={formData.branchId} onChange={handleInputChange} required>
                                                <option value="">Select Branch</option>
                                                {branches.map(b => <option key={b.id} value={b.id}>{b.branchName}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Project</label>
                                            <select name="projectId" className="form-control" value={formData.projectId} onChange={handleInputChange}>
                                                <option value="">Select Project</option>
                                                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Entity Type</label>
                                            <select name="entityType" className="form-control" value={formData.entityType} onChange={handleInputChange}>
                                                <option value="staff">Staff</option>
                                                <option value="member">Member</option>
                                                <option value="vendor">Vendor</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-8 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Select {formData.entityType.charAt(0).toUpperCase() + formData.entityType.slice(1)}</label>
                                            {formData.entityType === 'staff' && (
                                                <select name="staffId" className="form-control" value={formData.staffId} onChange={handleInputChange} required>
                                                    <option value="">Select Staff</option>
                                                    {staffs.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.staffCode})</option>)}
                                                </select>
                                            )}
                                            {formData.entityType === 'member' && (
                                                <select name="memberId" className="form-control" value={formData.memberId} onChange={handleInputChange} required>
                                                    <option value="">Select Member</option>
                                                    {members.map(m => <option key={m.id} value={m.id}>{m.memberName}</option>)}
                                                </select>
                                            )}
                                            {formData.entityType === 'vendor' && (
                                                <select name="vendorId" className="form-control" value={formData.vendorId} onChange={handleInputChange} required>
                                                    <option value="">Select Vendor</option>
                                                    {vendors.map(v => <option key={v.id} value={v.id}>{v.vendorName}</option>)}
                                                </select>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Expenditure Type</label>
                                            <select name="expenditureTypeId" className="form-control" value={formData.expenditureTypeId} onChange={handleInputChange} required>
                                                <option value="">Select Type</option>
                                                {expenditureTypes.map(t => <option key={t.id} value={t.id}>{t.expenditureTypeName}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Payment Date</label>
                                            <input type="date" name="paymentDate" className="form-control" value={formData.paymentDate} onChange={handleInputChange} required />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Amount (₹)</label>
                                            <input type="number" name="amount" className="form-control" value={formData.amount} onChange={handleInputChange} required />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Payment Mode</label>
                                            <select name="paymentMode" className="form-control" value={formData.paymentMode} onChange={handleInputChange}>
                                                <option value="Cash">Cash</option>
                                                <option value="Bank Transfer">Bank Transfer</option>
                                                <option value="UPI">UPI</option>
                                                <option value="Cheque">Cheque</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Transaction No</label>
                                            <input type="text" name="transactionNo" className="form-control" value={formData.transactionNo} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Remarks</label>
                                            <input type="text" name="remarks" className="form-control" value={formData.remarks} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Is Active</label>
                                            <select name="isActive" className="form-control" value={formData.isActive} onChange={handleInputChange}>
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button type="button" className="btn bg-gradient-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                                    <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                        {saving ? 'Saving...' : (editMode ? 'Update' : 'Save')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Payout;

